import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { CardWrapper } from 'foundations-components/transitional/components/Card';
import { HelpLink } from 'foundations-components/transitional/components/HelpLink';
import { Link } from 'foundations-components/transitional/components/Link';
import { Small } from 'foundations-components/transitional/components/Small';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import styled from 'foundations-theming/utils';
import { getI18nText } from 'pricing-pages-lib/utils/languageUtils';
import { getButtonVariantProps } from 'pricing-pages-ui-components/utils/buttonVariantUtils';
import { useMemo, useState } from 'react';
import PlanBuilderCTA from '../../features/purchaseMotions/components/PlanBuilderCTA';
import { useGlobalLoading } from '../../hooks/loadingHooks';
import { useAvailableCurrencyCodes, useDisplayedCurrencyCode } from '../../hooks/useCurrencies';
import { useAppSelector } from '../../reducers';
import { isUngatedForSeatsSelector } from '../../selectors/GatingSelectors';
import { planBuilderBreakdownDisplayProductConfigurationsSelector, planBuilderCheckoutProductConfigurationsSelector, planBuilderCostToGetStartedSelector, planBuilderHasCommonTermsSelector, planBuilderHasErrorSelector, planBuilderIsLoadingSelector, planBuilderProductConfigurationsSelector, planBuilderPurchaseOptionPaymentTermSelector, planBuilderTotalAnnualRecurringCalculatedPriceCostSelector, planBuilderTotalAnnualRecurringListPriceCostSelector, planBuilderTotalMonthlyRecurringCalculatedPriceCostSelector } from '../../selectors/PlanBuilderSelectors';
import { productForSkuIdSelector } from '../../selectors/ProductsSelectors';
import { requiredOnboardingChoiceSkuIdsSelector, requiredOnboardingSkuIdsSelector } from '../../selectors/RequiredOnboardingSelectors';
import { useQueryParamTerm } from '../../utils/urls/appQueryParamUtilsLegacy';
import { CREATE_A_BUNDLE } from '../../v2/constants/tracking/TrackingSources';
import { trackModalInteraction } from '../../v2/utils/tracking/trackingEventUtils';
import CurrencyPicker from '../common/CurrencyPicker';
import DetailedTermSelector from '../common/TermSelectors/DetailedTermSelector';
import SkeletonLoadWrapper from '../common/loading/SkeletonLoadWrapper';
import AsyncPricingBreakdownModal from '../common/pricing/AsyncPricingBreakdownModal/AsyncPricingBreakdownModal';
import EmptyShoppingCart from './EmptyShoppingCart';
import ErrorShoppingCart from './ErrorShoppingCart';
import RequiredOnboardingSubtextContainer from './RequiredOnboardingSubtextContainer';
import ShareLink from './ShareLink';
import ShoppingCartContainer from './ShoppingCart/ShoppingCartContainer';
import { SummaryColumnPriceDisplaySection } from './SummaryColumnPriceDisplaySection';
import { usePlanBuilderPurchaseMotions } from './usePlanBuilderPurchaseMotions';
// test id to use in acceptance tests
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SUMMARY_COLUMN_TEST_ID = 'plan-builder-summary-column';
const StyledGradientBar = styled.div.withConfig({
  displayName: "SummaryColumn__StyledGradientBar"
})(({
  theme
}) => {
  return {
    height: theme.space[100],
    backgroundColor: theme.color['fill-accent-orange-pressed'],
    backgroundImage: `linear-gradient(
      -303deg,
      ${theme.color['fill-accent-orange-default']},
      0%,
      ${theme.color['fill-accent-orange-default']} 40%,
      ${theme.color['fill-accent-orange-pressed']} 100%
    )`
  };
});
const RequiredOnboardingWrapper = styled.div.withConfig({
  displayName: "SummaryColumn__RequiredOnboardingWrapper"
})(({
  theme
}) => {
  return {
    textAlign: 'center',
    '&:not(:empty)': {
      marginTop: theme.space[300]
    }
  };
});
const SummaryColumn = ({
  shareLinkAnchor,
  hideCurrencyPicker = true,
  hubsSignupProductOverride,
  allowRemoveCartItems = true
}) => {
  const {
    setQueryParamTerm
  } = useQueryParamTerm();
  const productConfigurations = useAppSelector(planBuilderProductConfigurationsSelector);
  const breakdownDisplayProductConfigurations = useAppSelector(planBuilderBreakdownDisplayProductConfigurationsSelector);
  const checkoutProductConfigurations = useAppSelector(planBuilderCheckoutProductConfigurationsSelector);
  const isLoading = useAppSelector(planBuilderIsLoadingSelector);
  const hasError = useAppSelector(planBuilderHasErrorSelector);
  const isUngatedForSeats = useAppSelector(isUngatedForSeatsSelector);
  const availableCurrencies = useAvailableCurrencyCodes();
  const shouldShowCurrencyContainer = !hideCurrencyPicker && availableCurrencies && availableCurrencies.length > 1;
  const isAppReady = useGlobalLoading();
  const hasCommonTerms = useAppSelector(planBuilderHasCommonTermsSelector);
  const skuIds = useMemo(() => productConfigurations.map(({
    skuId
  }) => skuId), [productConfigurations]);
  const productNames = useAppSelector(state => skuIds.map(skuId => productForSkuIdSelector(state, {
    skuId
  }).apiName));
  const [isPriceBreakdownVisible, setIsPriceBreakdownVisible] = useState(false);
  const currencyCode = useDisplayedCurrencyCode();
  const purchaseMotions = usePlanBuilderPurchaseMotions();
  const onboardingChoiceSkuIds = useAppSelector(state => skuIds.map(skuId => requiredOnboardingChoiceSkuIdsSelector(state, {
    skuId
  })).flat().filter(skuId => !!skuId));
  const hasOnboardingChoices = onboardingChoiceSkuIds && !!onboardingChoiceSkuIds.length;
  const hasRequiredOnboarding = useAppSelector(state => skuIds.some(skuId => requiredOnboardingSkuIdsSelector(state, {
    skuId
  })));

  //If there's required onboarding, it's auto included in the cost
  //If there's choice onboarding, check if any choices have been added to the
  // breakdown display product configs already
  const includesOnboardingCost = hasRequiredOnboarding || breakdownDisplayProductConfigurations.some(config => onboardingChoiceSkuIds.includes(config.skuId));
  const paymentTerm = useAppSelector(planBuilderPurchaseOptionPaymentTermSelector);
  const popTotalMonthlyCalculatedPrice = useAppSelector(planBuilderTotalMonthlyRecurringCalculatedPriceCostSelector);
  const popTotalAnnualListPrice = useAppSelector(planBuilderTotalAnnualRecurringListPriceCostSelector);
  const popTotalAnnualCalculatedPrice = useAppSelector(planBuilderTotalAnnualRecurringCalculatedPriceCostSelector);
  const popCostToGetStarted = useAppSelector(planBuilderCostToGetStartedSelector);
  const hasUndefinedTotalPrice = popTotalMonthlyCalculatedPrice === undefined || popTotalAnnualListPrice === undefined || popTotalAnnualCalculatedPrice === undefined || popCostToGetStarted === undefined;
  if (!checkoutProductConfigurations.length) {
    return /*#__PURE__*/_jsxs(CardWrapper, {
      overflow: "hidden",
      flush: true,
      "data-selenium-id": SUMMARY_COLUMN_TEST_ID,
      children: [/*#__PURE__*/_jsx(StyledGradientBar, {}), /*#__PURE__*/_jsx(EmptyShoppingCart, {})]
    });
  }
  if (hasError) {
    return /*#__PURE__*/_jsxs(CardWrapper, {
      overflow: "hidden",
      flush: true,
      "data-selenium-id": SUMMARY_COLUMN_TEST_ID,
      children: [/*#__PURE__*/_jsx(StyledGradientBar, {}), /*#__PURE__*/_jsx(ErrorShoppingCart, {})]
    });
  }
  const isCheckingOutAnyProducts = checkoutProductConfigurations.length && checkoutProductConfigurations.some(({
    quantity
  }) => quantity > 0);
  const handlePriceBreakdownLinkClick = () => {
    trackModalInteraction({
      action: 'click to open',
      name: 'pricing-breakdown-modal',
      value: getI18nText('planBuilder.viewPriceBreakdownLink', {
        locale: 'en-us'
      }),
      trackingSource: CREATE_A_BUNDLE,
      productNames,
      skuIds
    });
    setIsPriceBreakdownVisible(true);
  };
  const SummaryColumnPriceDisplay = () => /*#__PURE__*/_jsx(View, {
    textAlign: "center",
    marginBlockStart: isUngatedForSeats ? '400' : undefined,
    marginBlockEnd: isUngatedForSeats ? undefined : '400',
    children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
      isLoading: isLoading || hasUndefinedTotalPrice || !paymentTerm,
      heights: [40],
      widths: ['100%'],
      children: /*#__PURE__*/_jsx(SummaryColumnPriceDisplaySection, {
        monthlyCostPrice: popTotalMonthlyCalculatedPrice,
        annualListPrice: popTotalAnnualListPrice,
        activeCurrencyCode: currencyCode,
        annualCostPrice: popTotalAnnualCalculatedPrice,
        term: paymentTerm,
        costToGetStarted: popCostToGetStarted,
        showTotalPriceAsterisk: hasOnboardingChoices || hasRequiredOnboarding
      })
    })
  });
  const ctaVariantProps = getButtonVariantProps({
    variant: 'block'
  });
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsxs(CardWrapper, {
      overflow: "hidden",
      flush: true,
      "data-selenium-id": SUMMARY_COLUMN_TEST_ID,
      children: [/*#__PURE__*/_jsx(StyledGradientBar, {}), /*#__PURE__*/_jsxs(Flex, {
        paddingInline: "800",
        paddingBlock: "600",
        flexDirection: "column",
        children: [shouldShowCurrencyContainer && /*#__PURE__*/_jsxs(TFlex, {
          direction: "row",
          justify: "center",
          align: "center",
          children: [/*#__PURE__*/_jsx(Text, {
            typography: "body-125",
            marginBlockEnd: "300",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "interstitial.currency"
            })
          }), /*#__PURE__*/_jsx(CurrencyPicker, {})]
        }), /*#__PURE__*/_jsx(View, {
          marginBlockEnd: isUngatedForSeats ? '400' : '100',
          minBlockSize: "36px",
          children: hasCommonTerms && paymentTerm && /*#__PURE__*/_jsx(DetailedTermSelector, {
            trackingSource: CREATE_A_BUNDLE,
            activeTerm: paymentTerm,
            onChange: setQueryParamTerm,
            productConfigurations: productConfigurations,
            compact: true
          })
        }), !isUngatedForSeats && /*#__PURE__*/_jsx(SummaryColumnPriceDisplay, {}), /*#__PURE__*/_jsx(ShoppingCartContainer, {
          allowRemoveCartItems: allowRemoveCartItems
        }), isUngatedForSeats && /*#__PURE__*/_jsx(SummaryColumnPriceDisplay, {}), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
          isLoading: !isAppReady,
          children: /*#__PURE__*/_jsx(Flex, {
            flexDirection: "column",
            justifyContent: "center",
            gap: "200",
            marginBlockStart: "1000",
            inlineSize: "100%",
            children: purchaseMotions.map((purchaseMotion, index) => {
              return /*#__PURE__*/_jsx("div", {
                children: /*#__PURE__*/_jsx(PlanBuilderCTA, Object.assign({
                  use: index === 0 ? 'primary' : 'secondary',
                  hubsSignupProductOverride: hubsSignupProductOverride,
                  purchaseMotion: purchaseMotion,
                  trackingSource: CREATE_A_BUNDLE
                }, ctaVariantProps))
              }, String(purchaseMotion));
            })
          })
        }), isCheckingOutAnyProducts && /*#__PURE__*/_jsx(View, {
          alignSelf: "center",
          marginBlockStart: "700",
          children: /*#__PURE__*/_jsx(Link, {
            onClick: handlePriceBreakdownLinkClick,
            onMouseEnter: () => {
              return void AsyncPricingBreakdownModal.preload();
            },
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "planBuilder.viewPriceBreakdownLink"
            })
          })
        }), shareLinkAnchor !== undefined && isCheckingOutAnyProducts && /*#__PURE__*/_jsx(View, {
          alignSelf: "center",
          marginBlockStart: "400",
          children: /*#__PURE__*/_jsx(ShareLink, {
            anchorId: shareLinkAnchor
          })
        }), paymentTerm && /*#__PURE__*/_jsx(RequiredOnboardingWrapper, {
          children: /*#__PURE__*/_jsx(RequiredOnboardingSubtextContainer, {
            queryParamTerm: paymentTerm,
            skuIds: skuIds,
            trackingSource: CREATE_A_BUNDLE,
            includesOnboardingCost: includesOnboardingCost
          })
        })]
      }), isPriceBreakdownVisible && /*#__PURE__*/_jsx(AsyncPricingBreakdownModal, {
        onCloseClick: () => setIsPriceBreakdownVisible(false),
        skuIds: skuIds
      })]
    }), /*#__PURE__*/_jsx(View, {
      marginBlockStart: "200",
      textAlign: "center",
      children: /*#__PURE__*/_jsx(HelpLink, {
        content: {
          body: /*#__PURE__*/_jsxs(View, {
            padding: "500",
            as: "div",
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "pricingPage.feeIncreasesProductServicesCatalog"
            }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(FormattedMessage, {
              message: "pricingPage.annualDiscountLegalDisclaimer"
            })]
          })
        },
        children: /*#__PURE__*/_jsx(Small, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "planBuilder.priceDisclaimer"
          })
        })
      })
    })]
  });
};
export default SummaryColumn;